/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

'use client';

import { forwardRef, useEffect, useState } from 'react';
import Image from 'next/image';
import { useLanguage } from '@/providers/LanguageProvider';
import { Badge } from '@/ux-common/src/components/ui/badge';
import Icons from '@/ux-common/src/icons/icons';
import { iconMap } from '@/ux-common/src/lib/helper';
import { cva } from 'class-variance-authority';
import { CheckIcon, ChevronDown, MinusIcon, WandSparkles } from 'lucide-react';
import { MultiSelectProps } from 'types/multi-select';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { System } from '../icons';
import BasicLoader from '../spinner/basic-loader';
import { DetailsPageSpinner } from '../spinner/details-page-spinner';
import { ScrollArea } from '../ui/scroll-area';

const WORD_COUNT_BY_PILL_LENGTH = 18;

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
export const multiSelectVariants = cva('m-1', {
  variants: {
    variant: {
      default: 'border-foreground/10 text-foreground bg-card hover:bg-card/80',
      secondary:
        'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
      destructive:
        'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
      inverted: 'inverted',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export const EnhancedMultiSelect = forwardRef<
  HTMLButtonElement,
  MultiSelectProps
>(
  (
    {
      options = [],
      onValueChange,
      variant,
      defaultValue = [],
      placeholder,
      animation = 0,
      maxCount = 2,
      modalPopover = false,
      className,
      loading,
      allCheckbox,
      showPills = true,
      inputPlaceholder,
      hasDelete = false,
      asChild = false,
      children,
      maxOptions,
      onOpenChange,
      ...props
    },
    ref
  ) => {
    const [selectedValues, setSelectedValues] = useState<string[]>(
      defaultValue ?? []
    );
    const [confirmedValues, setConfirmedValues] = useState(selectedValues);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    const { messages } = useLanguage();
    const langData = messages?.PromptFilters;

    useEffect(() => {
      if (!loading) setSelectedValues(defaultValue);
    }, [loading]);

    useEffect(() => {
      if (hasDelete) {
        setSelectedValues(defaultValue);
      }
    }, [isPopoverOpen, hasDelete]);

    const toggleOption = (option: string) => {
      const newSelectedValues = selectedValues.includes(option)
        ? selectedValues.filter((value) => value !== option)
        : [...selectedValues, option];
      setSelectedValues(newSelectedValues);
    };

    const toggleOptionInline = (option: string) => {
      const newSelectedValues = confirmedValues.includes(option)
        ? confirmedValues.filter((value) => value !== option)
        : [...confirmedValues, option];
      setSelectedValues(newSelectedValues);
      setConfirmedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleClear = () => {
      setSelectedValues([]);
      setConfirmedValues([]);
      onValueChange([]);
    };

    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev);
    };

    const toggleAll = () => {
      if (selectedValues.length === options.length) {
        handleClear();
      } else {
        const allValues = options.map((option) => option.value);
        // Respect maxOptions when selecting all
        const valuesToSelect = maxOptions
          ? allValues.slice(0, maxOptions)
          : allValues;
        setSelectedValues(valuesToSelect);
      }
    };

    const handleApply = () => {
      setConfirmedValues(selectedValues);
      onValueChange(selectedValues);
      setIsPopoverOpen(false);
    };

    const renderTrigger = () => {
      if (showPills) {
        return (
          <Button
            ref={ref}
            {...props}
            onClick={handleTogglePopover}
            className={cn(
              'flex h-auto min-h-10 w-full items-center justify-between rounded-sm border border-form-control-border-rest bg-inherit p-1 hover:bg-inherit',
              className
            )}
            disabled={loading}
          >
            {confirmedValues.length > 0 ? (
              <div className="flex w-full items-center justify-between">
                {allCheckbox && selectedValues.length === options.length ? (
                  <span className="mx-2.5">{langData?.multi_select.all}</span>
                ) : (
                  <div className="flex flex-wrap items-center gap-1">
                    {confirmedValues.slice(0, maxCount).map((value) => {
                      const option = options.find((o) => o.value === value);
                      const IconComponent = option?.icon;
                      const wordLen = option?.label.length;
                      const itemKey = option?.id ? String(option.id) : value;
                      return (
                        <Badge
                          key={itemKey}
                          className={cn(
                            '!m-0 h-6 max-w-[140px] rounded bg-[#0A343C] font-normal text-body-primary hover:bg-[#0A343C]',

                            isAnimating ? 'animate-bounce' : '',
                            multiSelectVariants({ variant })
                          )}
                          style={{ animationDuration: `${animation}s` }}
                        >
                          {IconComponent && (
                            <IconComponent className="mr-2 size-4" />
                          )}

                          {wordLen && wordLen > WORD_COUNT_BY_PILL_LENGTH ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className={cn(
                                      'text-sm text-body-primary',
                                      wordLen &&
                                        wordLen > WORD_COUNT_BY_PILL_LENGTH &&
                                        'truncate'
                                    )}
                                  >
                                    {option?.label}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>{option?.label}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-sm text-body-primary">
                              {option?.label}
                            </span>
                          )}
                          <span
                            className="ml-2 shrink-0 cursor-pointer text-form-control-border-rest hover:text-body-primary"
                            onClick={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              toggleOptionInline(value);
                            }}
                          >
                            {Icons.Close('size-3')}
                          </span>
                        </Badge>
                      );
                    })}
                    {confirmedValues.length > maxCount && (
                      <Badge
                        className={cn(
                          'h-5 rounded bg-transparent px-1.5 text-sm font-normal text-body-subtle hover:bg-transparent',
                          isAnimating ? 'animate-bounce' : '',
                          multiSelectVariants({ variant })
                        )}
                        style={{ animationDuration: `${animation}s` }}
                      >
                        {`+${confirmedValues.length - maxCount} more`}
                      </Badge>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span
                    className="mx-2 cursor-pointer text-fill-form-control-disabled hover:text-body-primary"
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      handleClear();
                    }}
                  >
                    {Icons.Close('size-3')}
                  </span>
                  <Separator
                    orientation="vertical"
                    className="h-full min-h-4 bg-form-control-border-disabled"
                  />
                  <ChevronDown
                    strokeWidth={2}
                    className="mx-2 size-4 cursor-pointer text-form-input-rest"
                  />
                </div>
              </div>
            ) : (
              <div className="mx-auto flex w-full min-w-0 items-center justify-between gap-2">
                {loading ? (
                  <div className="mx-2 flex min-w-0 flex-1 items-center gap-2 text-left">
                    <BasicLoader className="size-4 shrink-0" />
                    <span className="truncate text-sm text-form-input-rest">
                      {messages?.General?.loading}
                    </span>
                  </div>
                ) : (
                  <span className="ml-3 min-w-0 flex-1 truncate text-left text-sm text-form-input-rest">
                    {placeholder}
                  </span>
                )}
                <ChevronDown className="mx-2 size-4 shrink-0 cursor-pointer text-form-input-rest" />
              </div>
            )}
          </Button>
        );
      } else {
        return (
          <div className=" flex h-[42px] w-full items-center justify-between rounded-sm border border-form-control-border-rest">
            {loading ? (
              <div className="mx-2 flex min-w-0 flex-1 items-center gap-2 text-left">
                <BasicLoader className="size-4 shrink-0" />
                <span className="truncate text-sm text-[#8C9EA1]">
                  {messages?.General?.loading}
                </span>
              </div>
            ) : (
              <span className="ml-3 min-w-0 flex-1 truncate text-sm text-[#8C9EA1]">
                {inputPlaceholder}
              </span>
            )}
            <ChevronDown className="mx-2 size-4 shrink-0 cursor-pointer text-form-input-rest" />
          </div>
        );
      }
    };

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={(isOpen) => {
          setIsPopoverOpen(isOpen);
          if (!isOpen) {
            setSelectedValues(confirmedValues);
          }
          // Call the external onOpenChange handler if provided
          onOpenChange?.(isOpen);
        }}
        modal={modalPopover}
      >
        <PopoverTrigger asChild>
          {asChild && children ? <div>{children}</div> : renderTrigger()}
        </PopoverTrigger>
        <PopoverContent
          className="-mt-2 w-auto p-0"
          align="start"
          sideOffset={8}
          onEscapeKeyDown={() => {
            setIsPopoverOpen(false);
          }}
        >
          <Command className="rounded-sm rounded-b-none bg-white">
            <CommandInput
              placeholder={
                inputPlaceholder ?? `${langData?.multi_select.placeholder}`
              }
              iconColor="text-form-input-disabled"
              className="text-[#323130] placeholder:text-[#8C9EA1]"
            />

            <CommandList className="bg-white">
              <CommandEmpty className="m-3 text-center text-black">
                {loading ? (
                  <DetailsPageSpinner />
                ) : options?.length === 0 ? (
                  <p>{messages?.General?.no_data_found}</p>
                ) : (
                  langData?.multi_select.no_results_found
                )}
              </CommandEmpty>
              {allCheckbox && (
                <CommandGroup className="pb-0 pt-1 text-[#323130]">
                  <CommandItem
                    key="all"
                    onSelect={toggleAll}
                    className="cursor-pointer data-[selected=true]:bg-[#EDEBE9] data-[selected=true]:text-[#323130] "
                  >
                    <div
                      className={cn(
                        'mr-2 flex size-4 items-center justify-center rounded-sm bg-primary text-primary-foreground',
                        selectedValues.length === 0 &&
                          'border border-[#323130] bg-transparent opacity-50 [&_svg]:invisible'
                      )}
                    >
                      {selectedValues.length === options.length ||
                      (maxOptions && selectedValues.length === maxOptions) ? (
                        <CheckIcon className="size-4 rounded-sm border border-jade bg-jade text-white" />
                      ) : (
                        <MinusIcon className="size-4 rounded-sm border border-jade bg-jade text-white" />
                      )}
                    </div>
                    <span>
                      ({langData?.multi_select.select_all}
                      {maxOptions &&
                        ` - ${messages?.General?.max} ${maxOptions}`}
                      )
                    </span>
                  </CommandItem>
                </CommandGroup>
              )}
              {/* info row below all options. */}
              {maxOptions && selectedValues.length >= maxOptions && (
                <>
                  <div className="mx-3 flex items-center gap-2">
                    {System.InfoIcon(
                      'w-4 h-4 text-form-input-text text-form-input-disabled'
                    )}
                    <p className="py-2 text-form-input-text text-form-input-disabled">
                      {messages?.Evaluation.max_criteria_msg_1} {maxOptions}{' '}
                      {messages?.Evaluation.max_criteria_msg_2}
                    </p>
                  </div>

                  <Separator className="bg-[#C1C9CF]" />
                </>
              )}
              {options?.length > 0 && (
                <CommandGroup className="text-[#323130]">
                  <ScrollArea className="h-40">
                    {options.map((option, index) => {
                      const isSelected = selectedValues.includes(option.value);

                      return (
                        <CommandItem
                          key={`${option.value} + ${index}`}
                          value={`${option.value} ${option.label}`}
                          onSelect={() => toggleOption(option.value)}
                          className="cursor-pointer data-[selected=true]:bg-[#EDEBE9] data-[selected=true]:text-[#323130]"
                        >
                          <div
                            className={cn(
                              'mr-2 flex size-4 items-center justify-center rounded-sm border border-[#323130]',
                              isSelected
                                ? 'border border-jade bg-jade text-primary-foreground'
                                : 'opacity-50 [&_svg]:invisible'
                            )}
                          >
                            <CheckIcon className="size-4 border border-jade text-white" />
                          </div>
                          {option.icon && (
                            <option.icon className="mr-2 size-4 text-muted-foreground" />
                          )}
                          {option.iconType && (
                            <Image
                              className="mr-1 size-5"
                              src={
                                iconMap[option.iconType] ?? iconMap['default']
                              }
                              alt="Logo"
                              width={100}
                              height={100}
                            />
                          )}
                          <p className="grid w-full grid-cols-2">
                            {option.tooltip ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span>{option.label}</span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {option.tooltip}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <span>{option.label}</span>
                            )}
                            <span className="text-sm text-muted-foreground">
                              {option.time}
                            </span>
                          </p>
                        </CommandItem>
                      );
                    })}
                  </ScrollArea>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
          <div className="rounded-b-sm bg-white">
            <Separator className="bg-[#C1C9CF]" />
            <div className="text-btn-label text-[#0A2D3A]">
              <div className="flex items-center justify-between">
                <>
                  {(selectedValues.length > 0 ||
                    defaultValue.length !== selectedValues.length) && (
                    <>
                      <Button
                        type="button"
                        variant={'ghost'}
                        onClick={handleApply}
                        className="mx-2 my-1 h-7 flex-1 cursor-pointer justify-center hover:bg-[#EDEBE9] hover:text-[#0A2D3A]"
                        disabled={
                          maxOptions
                            ? selectedValues.length > maxOptions
                            : false
                        }
                      >
                        {langData?.multi_select.apply}
                      </Button>
                      <Separator
                        orientation="vertical"
                        className="h-full min-h-4 bg-line-secondary"
                      />
                    </>
                  )}
                  {selectedValues.length > 0 && (
                    <>
                      <Button
                        type="button"
                        variant={'ghost'}
                        onClick={handleClear}
                        className="mx-2 my-1 h-7 flex-1 cursor-pointer justify-center hover:bg-[#EDEBE9] hover:text-[#0A2D3A]"
                      >
                        {langData?.multi_select.clear}
                      </Button>
                      <Separator
                        orientation="vertical"
                        className="h-full min-h-4 bg-line-secondary"
                      />
                    </>
                  )}
                </>

                <Button
                  type="button"
                  variant={'ghost'}
                  onClick={() => {
                    setIsPopoverOpen(false);
                    if (confirmedValues !== selectedValues) {
                      setSelectedValues(confirmedValues);
                    }
                  }}
                  className="mx-2 my-1 h-7 flex-1 cursor-pointer justify-center hover:bg-[#EDEBE9] hover:text-[#0A2D3A]"
                >
                  {messages?.General?.close}
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
        {animation > 0 && selectedValues.length > 0 && (
          <WandSparkles
            className={cn(
              'my-2 h-3 w-3 cursor-pointer bg-background text-foreground',
              isAnimating ? '' : 'text-muted-foreground'
            )}
            onClick={() => setIsAnimating(!isAnimating)}
          />
        )}
      </Popover>
    );
  }
);

EnhancedMultiSelect.displayName = 'EnhancedMultiSelect';
